import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EvServiceInstallationData } from "../..";
import { useCreateEvChargerServiceMutation } from "generated/graphql";
import useFilteredInstallationData from "../../utility/useFilterUncheckedFields";
import WizardPageLayout from "../../components/WizardPageLayout";
import WizardButton from "../../components/WizardButton";
import ReviewSummary from "../../components/ReviewSummary";

const SUMMARY_WIDTH = 640;

interface ServiceOfferingSetupProps {
  data: EvServiceInstallationData;
}

const ReviewAndSubmit: React.FC<ServiceOfferingSetupProps> = ({ data }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("services");
  const { filterInstallationData } = useFilteredInstallationData();

  const [{ fetching }, createEVChargerService] =
    useCreateEvChargerServiceMutation();

  const handleSubmit = useCallback(async () => {
    try {
      const result = await createEVChargerService(
        { options: { data: filterInstallationData(data) } },
        { requestPolicy: "network-only" },
      );

      if (result.error) {
        console.error("Error creating EV charger service:", result.error);
        // You might want to show a user-friendly error message here
        return;
      }

      if (result.data) {
        // Refetch the services list to get updated data
        navigate("/dashboard/serviceOfferings");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  }, [data, createEVChargerService, filterInstallationData, navigate]);

  return (
    <WizardPageLayout
      title={t("wizard.review.launch_title")}
      contentMaxWidth={SUMMARY_WIDTH}
      actions={
        <>
          <WizardButton
            color="primary"
            onClick={() => navigate("/dashboard/serviceOfferings")}
          >
            {t("wizard.cancel")}
          </WizardButton>

          <WizardButton
            color="warning"
            onClick={handleSubmit}
            loading={fetching}
          >
            {t("wizard.review.launch")}
          </WizardButton>
        </>
      }
    >
      <ReviewSummary
        data={data}
        onEdit={(page) => navigate(`?currentPage=${page}&editMode=true`)}
      />
    </WizardPageLayout>
  );
};

export default ReviewAndSubmit;
